import { addDays, format, parseISO } from 'date-fns';
import {
  CandidateBooleanSelectionFilter,
  CandidateFilterCondition,
  CandidateFilterGroups,
  CandidateFilterMatchMode,
  CandidateFilterNode,
  CandidateFilterState,
  CandidateFilterValueType,
  CandidateGeoSelectionFilter,
  CandidateRangeFilter,
  CandidateSelectionFilter,
  CandidateTextArrayFilter,
  CandidateTextFilter,
  SourcingFilterField
} from 'modules/candidate-profile/types/CandidateSearchFilter';
import { chipGroupsInOrder } from 'modules/candidate-profile/tables/filters/candidateFilterGroups';
import { UNIVERSAL_FILTER_FIELDS } from 'modules/candidate-profile/tables/filters/universalFilterFields';

const MODE_OPERATORS: Record<
  CandidateFilterMatchMode,
  keyof CandidateTextArrayFilter
> = {
  [CandidateFilterMatchMode.INCLUDE_ANY]: 'containsAny',
  [CandidateFilterMatchMode.INCLUDE_ALL]: 'containsAll',
  [CandidateFilterMatchMode.EXCLUDE]: 'containsNone'
};

const withKey = (
  filter: { field: string; vectorDbKey?: string },
  operators: Partial<CandidateFilterCondition>
): CandidateFilterCondition => ({
  field: filter.field,
  ...(filter.vectorDbKey ? { vectorDbKey: filter.vectorDbKey } : {}),
  ...operators
});

export const isFreeTextField = (
  field: string,
  availableFilters: SourcingFilterField[]
): boolean => {
  const available = availableFilters.find((each) => each.field === field);
  return (
    available?.type === CandidateFilterValueType.TEXT &&
    !available.allowedValues?.length
  );
};

const isUniversalFreeTextField = (field: string) =>
  isFreeTextField(field, UNIVERSAL_FILTER_FIELDS);

const buildSelectionCondition = (
  filter: CandidateSelectionFilter,
  isFreeText: (field: string) => boolean
): CandidateFilterNode | null => {
  if (!filter.values.length) return null;

  const operator = MODE_OPERATORS[filter.mode];
  const isText = filter.type === CandidateFilterValueType.TEXT;

  // Free text matches a substring: `containsAny` is equality on the service,
  // and "London" would miss "Greater London".
  if (
    isText &&
    isFreeText(filter.field) &&
    filter.mode === CandidateFilterMatchMode.INCLUDE_ANY
  ) {
    return filter.values.length === 1
      ? withKey(filter, { text: { like: filter.values[0] } })
      : withKey(filter, { text: { likeAny: filter.values } });
  }

  if (filter.type === CandidateFilterValueType.TEXT_ARRAY) {
    return withKey(filter, { textArray: { [operator]: filter.values } });
  }

  // containsAll is unsatisfiable on a single-valued property.
  const text: CandidateTextFilter =
    filter.mode === CandidateFilterMatchMode.EXCLUDE
      ? { containsNone: filter.values }
      : { containsAny: filter.values };

  return withKey(filter, { text });
};

const nextCalendarDay = (date: string): string =>
  format(addDays(parseISO(date.slice(0, 10)), 1), 'yyyy-MM-dd');

const buildRangeCondition = (
  filter: CandidateRangeFilter
): CandidateFilterCondition | null => {
  const hasMin = filter.min !== undefined && filter.min !== null;
  const hasMax = filter.max !== undefined && filter.max !== null;
  if (!hasMin && !hasMax) return null;

  if (filter.type === CandidateFilterValueType.DATE) {
    // A date is stored as a timestamp, so the last day ends at the next one.
    return withKey(filter, {
      date: {
        ...(hasMin ? { greaterOrEqual: filter.min as string } : {}),
        ...(hasMax ? { lessThan: nextCalendarDay(filter.max as string) } : {})
      }
    });
  }

  return withKey(filter, {
    number: {
      ...(hasMin ? { greaterOrEqual: filter.min as number } : {}),
      ...(hasMax ? { lessOrEqual: filter.max as number } : {})
    }
  });
};

const buildBooleanCondition = (
  filter: CandidateBooleanSelectionFilter
): CandidateFilterCondition =>
  withKey(filter, { boolean: { equal: filter.value } });

const buildGeoCondition = (
  filter: CandidateGeoSelectionFilter
): CandidateFilterCondition =>
  withKey(filter, {
    geo: {
      latitude: filter.latitude,
      longitude: filter.longitude,
      radiusMiles: filter.radiusMiles,
      ...(filter.name ? { name: filter.name } : {})
    }
  });

const toCondition = (
  filter: CandidateFilterState,
  isFreeText: (field: string) => boolean
): CandidateFilterNode | null => {
  switch (filter.type) {
    case CandidateFilterValueType.TEXT:
    case CandidateFilterValueType.TEXT_ARRAY:
      return buildSelectionCondition(filter, isFreeText);
    case CandidateFilterValueType.NUMBER:
    case CandidateFilterValueType.DATE:
      return buildRangeCondition(filter);
    case CandidateFilterValueType.BOOLEAN:
      return buildBooleanCondition(filter);
    case CandidateFilterValueType.GEO:
      return buildGeoCondition(filter);
    default:
      return null;
  }
};

const isNode = (
  node: CandidateFilterNode | null
): node is CandidateFilterNode => node !== null;

const toChipGroupNode = (
  chipGroup: CandidateFilterState[],
  isFreeText: (field: string) => boolean
): CandidateFilterNode | null => {
  const conditions = chipGroup
    .map((filter) => toCondition(filter, isFreeText))
    .filter(isNode);

  if (conditions.length === 0) return null;
  if (conditions.length === 1) return conditions[0];
  return { or: conditions };
};

/**
 * Sourcing filter state -> the request's filter tree.
 *
 * "Filter tree" is the backend's name for the request shape
 * (`CandidateSearchFilterInput`): `and`, `or` and `not` over typed conditions.
 */
export const buildCandidateFilterTree = (
  filters: CandidateFilterState[],
  groups: CandidateFilterGroups = [],
  isFreeText: (field: string) => boolean = isUniversalFreeTextField
): CandidateFilterNode | null => {
  const children = chipGroupsInOrder(filters, groups)
    .map((chipGroup) => toChipGroupNode(chipGroup, isFreeText))
    .filter(isNode);

  if (!children.length) return null;
  return children.length === 1 ? children[0] : { and: children };
};
